/**
 * This is a utility to handle default API requests with the Yote server
 */

import _ from 'lodash'
// TODO: break this into separate exports so we aren't forced to import the entire set to use one method.

function resolveApiUrl(route) {
  const origin = process.env.REACT_APP_API_ORIGIN;
  if (origin) {
    const base = origin.replace(/\/$/, '');
    return route.startsWith('/') ? `${base}${route}` : `${base}/${route}`;
  }
  return route;
}

function isPdfArrayBuffer(arrayBuffer) {
  const view = new Uint8Array(arrayBuffer);
  return view.length >= 4
    && view[0] === 0x25
    && view[1] === 0x50
    && view[2] === 0x44
    && view[3] === 0x46;
}

function describeNonPdfBody(arrayBuffer) {
  try {
    const text = new TextDecoder().decode(arrayBuffer.slice(0, 200)).trim();
    if (text.startsWith('<') || text.startsWith('{')) {
      return 'Server returned HTML/JSON instead of a PDF. Check the API response or try REACT_APP_API_ORIGIN=http://localhost:3030.';
    }
    if (text.length) {
      return text.slice(0, 120);
    }
  } catch {
    // ignore
  }
  return 'Server response is not a valid PDF file.';
}

const apiUtils = {
  async callAPI(route, method = 'GET', body, headers = {
    'Accept': 'application/json', 'Content-Type': 'application/json',
  }) {
    const response = await fetch(route, {
      headers,
      method,
      credentials: 'same-origin',
      body: JSON.stringify(body)
    });
    if (response.ok) {
      return response.json();
    } else {
      const error = await response.json().catch(unhandledError => {
        console.error('Unhandled Error Thrown by Server. Not cool.', unhandledError);
        throw 'Something went wrong';
      });
      throw error;
    }
  },
  async downloadBlobAPI(route, method = 'POST', body, headers = {
    'Accept': 'application/pdf',
    'Content-Type': 'application/json',
  }) {
    return apiUtils.downloadPdfAPI(route, method, body, headers);
  },
  async downloadPdfAPI(route, method = 'POST', body, headers = {
    'Accept': 'application/pdf',
    'Content-Type': 'application/json',
  }) {
    const url = resolveApiUrl(route);
    const response = await fetch(url, {
      headers,
      method,
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      let error = { message: text || 'Something went wrong' };
      try {
        error = JSON.parse(text);
      } catch {
        // server may return plain-text YoteError messages
      }
      throw error;
    }
    const arrayBuffer = await response.arrayBuffer();

    if (!isPdfArrayBuffer(arrayBuffer)) {
      throw new Error(describeNonPdfBody(arrayBuffer));
    }

    const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
    const disposition = response.headers.get('Content-Disposition') || '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
    const filename = filenameMatch ? filenameMatch[1] : 'assessment-print.pdf';
    return { blob, filename };
  },
  async rawAPI(route, method = 'GET', body, headers = {}) {
    const response = await fetch(route, {
      headers,
      method,
      credentials: 'same-origin',
      body
    });
    if (response.ok) {
      return response.json();
    } else {
      const error = await response.json().catch(unhandledError => {
        console.error('Unhandled Error Thrown by Server. Not cool.', unhandledError);
        throw 'Something went wrong';
      });
      throw error;
    }
  },
  queryStringFromObject(queryObject) {
    if (!queryObject) return "";
    if (typeof queryObject !== "object") return "";
    if (Object.keys(queryObject).length === 0) return "";
    return Object.entries(queryObject)
        .filter(entry => entry[1] && entry[1].toString().length > 0)
        .map(item => {
          return item;
        })
        .map(entry => Array.isArray(entry[1]) ? [entry[0], entry[1].join(",")] : entry)
        .map(entry => entry.join("="))
        .join("&");
  },
  objectFromQueryString(queryString) {
    queryString = queryString?.split("?")[1];
    if (!queryString) return {};
    return queryString.replace("?", "").split("&")
        .map(item => item.split("="))
        .map(item => [_.camelCase(item[0]), item[1]])
        .reduce((returnObj, item) => { return item[0].length > 0 ? { ...returnObj, [item[0]]: item[1] } : returnObj }, {});
  },
  checkListArgsReady(listArgs) {
    if (!listArgs) return false;
    if (typeof listArgs !== "object") return false;
    let listArgsReady = true;
    Object.keys(listArgs).forEach(key => {
      if ((listArgs[key] === undefined) || (Array.isArray(listArgs[key]) && listArgs[key].length === 0)) {
        listArgsReady = false;
      }
    });
    return listArgsReady;
  }
}

export default apiUtils;
